import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ToggleOptionCard } from './toggleOptionCard';
import Loader from 'components/v2/common/loader';

import api from 'api';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import type { RootState } from 'redux/store';
import { setEnabledCountryCodes } from 'redux/user/user-slice';
import type { CountryCode, ServiceProviderAdminRow } from 'types';
import { COUNTRIES, countryLabelKey, normalizeEnabledCountryCodes } from 'utils/countries';
import { getErrorMessage } from 'utils/v2/errorHelper';

interface ServiceProviderCountriesDialogProps {
    provider: ServiceProviderAdminRow;
    onClose: () => void;
    onUpdated: (providerId: number, enabledCountryCodes: CountryCode[]) => void;
}

const COUNTRY_DESCRIPTION_KEY: Record<CountryCode, string> = {
    AU: 'australiaCountryDescription',
    CA: 'canadaCountryDescription',
    UK: 'unitedKingdomCountryDescription',
};

export function ServiceProviderCountriesDialog({
    provider,
    onClose,
    onUpdated,
}: ServiceProviderCountriesDialogProps) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const dispatch = useDispatch();
    const { translate } = useTranslate();
    const currentProviderId = useSelector(
        (state: RootState) => state.user.entity.service_provider_id
    );

    const [savedCodes, setSavedCodes] = useState<CountryCode[]>([]);
    const [selected, setSelected] = useState<CountryCode[]>([]);
    const [didLoad, setDidLoad] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setIsLoading(true);
            setDidLoad(false);
            try {
                const response = await api.serviceProvider.getCountries(provider.id);
                if (cancelled) return;
                const codes = normalizeEnabledCountryCodes(response.data.enabled_country_codes);
                setSavedCodes(codes);
                setSelected(codes);
                setDidLoad(true);
            } catch (error) {
                if (cancelled) return;
                dispatch(
                    setAlert({
                        message: getErrorMessage(error, translate('somethingWentWrong')),
                        type: 'error',
                    })
                );
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        void load();
        return () => {
            cancelled = true;
        };
    }, [dispatch, provider.id, translate]);

    const selectedCodes = useMemo(() => normalizeEnabledCountryCodes(selected), [selected]);
    const hasChanges = selectedCodes.join('|') !== savedCodes.join('|');
    const canSave = didLoad && !isLoading && !isSaving && hasChanges && selectedCodes.length > 0;

    const handleToggle = (code: CountryCode) => {
        setSelected((prev) =>
            prev.includes(code) ? prev.filter((item) => item !== code) : [...prev, code]
        );
    };

    const handleClose = () => {
        if (!isSaving) onClose();
    };

    const handleSave = async () => {
        if (!canSave) return;
        setIsSaving(true);
        try {
            const response = await api.serviceProvider.updateCountries(provider.id, {
                enabled_country_codes: selectedCodes,
            });
            const updated = normalizeEnabledCountryCodes(response.data.enabled_country_codes);
            if (provider.id === currentProviderId) {
                dispatch(setEnabledCountryCodes(updated));
            }
            dispatch(setAlert({ message: translate('countriesUpdated'), type: 'success' }));
            onUpdated(provider.id, updated);
            onClose();
        } catch (error) {
            setIsSaving(false);
            dispatch(
                setAlert({
                    message: getErrorMessage(error, translate('somethingWentWrong')),
                    type: 'error',
                })
            );
        }
    };

    return (
        <Dialog
            open
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
            fullScreen={fullScreen}
            scroll="paper"
        >
            <DialogTitle>
                {translate('manageCountries')}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {provider.company_name}
                </Typography>
            </DialogTitle>
            <DialogContent dividers>
                {isLoading ? (
                    <Loader isCircular />
                ) : didLoad ? (
                    <Stack spacing={1.5}>
                        <Typography variant="body2" color="text.secondary">
                            {translate('countriesAdminDescription')}
                        </Typography>
                        {COUNTRIES.map((country) => (
                            <ToggleOptionCard
                                key={country.code}
                                title={translate(countryLabelKey(country.code))}
                                description={translate(COUNTRY_DESCRIPTION_KEY[country.code])}
                                selected={selected.includes(country.code)}
                                disabled={isSaving}
                                onToggle={() => handleToggle(country.code)}
                            />
                        ))}
                        {selectedCodes.length === 0 ? (
                            <Typography variant="body2" color="error">
                                {translate('selectAtLeastOneCountry')}
                            </Typography>
                        ) : null}
                    </Stack>
                ) : null}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                <Button
                    variant="outlined"
                    onClick={handleClose}
                    disabled={isSaving}
                    sx={{ textTransform: 'none' }}
                >
                    {translate('cancel')}
                </Button>
                <Button
                    variant="contained"
                    onClick={() => void handleSave()}
                    disabled={!canSave}
                    sx={{ textTransform: 'none' }}
                >
                    {translate('save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default ServiceProviderCountriesDialog;
